// League data kept in memory for the tab's session: the last complete response
// per request key, and at most one request in flight per key.
const MAX_ENTRIES = 50;
const entries = new Map();
const inFlight = new Map();
// Requests a clear stopped sharing: their responses are older than the clear.
const superseded = new WeakSet();

/** The remembered response for `key`, as `{ data }`, or undefined. */
export function readCache(key) {
  return entries.get(key);
}

function remember(key, data) {
  entries.delete(key);
  entries.set(key, { data });
  while (entries.size > MAX_ENTRIES) entries.delete(entries.keys().next().value);
}

/** Runs `fetchFn` unless a request for `key` is already running, in which case
 * callers share that one. */
export function shareInFlight(key, fetchFn) {
  const running = inFlight.get(key);
  if (running) return running;
  const request = Promise.resolve()
    .then(fetchFn)
    .finally(() => {
      if (inFlight.get(key) === request) inFlight.delete(key);
    });
  inFlight.set(key, request);
  return request;
}

/** Like shareInFlight, and a successful response is remembered under `key`
 * unless a clear superseded its request. */
export function fetchShared(key, fetchFn) {
  const request = shareInFlight(key, fetchFn);
  return request.then((data) => {
    if (!superseded.has(request)) remember(key, data);
    return data;
  });
}

/** Forgets every remembered response for one league, and stops sharing its
 * running requests, so the next fetch asks the server again. */
export function clearLeagueCache(leagueId) {
  const prefix = `/leagues/${leagueId}/`;
  for (const key of [...entries.keys()]) {
    if (key.includes(prefix)) entries.delete(key);
  }
  for (const [key, request] of [...inFlight.entries()]) {
    if (key.includes(prefix)) {
      superseded.add(request);
      inFlight.delete(key);
    }
  }
}

// Keys carry the client's API base (`api.baseUrl`), so a member's responses
// and a share link's never share an entry or a running request. The `cache:`
// prefix keeps them apart from the request URLs createLeagueApi shares in
// flight: a cached fetch whose key equalled its own request URL would wait on
// itself.
export function dashboardKey(apiBase, leagueId, season) {
  return `cache:${apiBase}/leagues/${leagueId}/dashboard?season=${season ?? 'live'}`;
}

export function playoffOddsKey(apiBase, leagueId, season) {
  return `cache:${apiBase}/leagues/${leagueId}/playoff-odds?season=${season ?? 'live'}`;
}

export function historyKey(apiBase, leagueId, through = null) {
  return `cache:${apiBase}/leagues/${leagueId}/history${through == null ? '' : `?through=${through}`}`;
}
